import { CSSProperties } from 'react';
import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { EPanelDirections, EPreviewSize, LayoutGridState, SetLayoutGridState, TLayoutBodyAttributes } from './types';
import updateLayoutGridStateField from './helpers/update-layout-grid-state-field';

export const reducers = (create: ReducerCreators<LayoutGridState>) => ({
  changeLayoutDirection: create.reducer((state, action: PayloadAction<EPanelDirections>) => {
    state.direction = action.payload;
  }),
  togglePanel: create.reducer((state) => {
    state.panelOpen = !state.panelOpen;
  }),
  toggleOppositePanel: create.reducer((state) => {
    state.oppositePanelOpen = !state.oppositePanelOpen;
  }),
  toggleFooter: create.reducer((state) => {
    state.footerOpen = !state.footerOpen;
  }),
  togglePillar: create.reducer((state) => {
    state.pillarOpen = !state.pillarOpen;
  }),
  toggleHeader: create.reducer((state) => {
    state.headerOpen = !state.headerOpen;
  }),
  setViewSize: create.reducer((state, action: PayloadAction<EPreviewSize>) => {
    state.preFullScreenState = {
      direction: state.direction,
      panelOpen: state.panelOpen,
      oppositePanelOpen: state.oppositePanelOpen,
      footerOpen: state.footerOpen,
      pillarOpen: state.pillarOpen,
      headerOpen: state.headerOpen,
      previewSize: state.previewSize !== EPreviewSize.FULL ? state.previewSize : EPreviewSize.DESKTOP,
    };

    state.previewSize = action.payload;

    if (action.payload === EPreviewSize.FULL) {
      state.panelOpen = false;
      state.oppositePanelOpen = false;
      state.footerOpen = false;
      state.pillarOpen = false;
      state.headerOpen = false;
    }
  }),
  changeGridLayouts: create.reducer(
    (
      state,
      action: PayloadAction<{ ltr: CSSProperties['gridTemplateAreas']; rtl: CSSProperties['gridTemplateAreas'] }>
    ) => {
      state.gridLayoutLTR = action.payload.ltr;
      state.gridLayoutRTL = action.payload.rtl;
    }
  ),
  resetPreFullscreenState: create.reducer((state) => {
    if (state.preFullScreenState) {
      state.direction = state.preFullScreenState.direction;
      state.panelOpen = state.preFullScreenState.panelOpen;
      state.oppositePanelOpen = state.preFullScreenState.oppositePanelOpen;
      state.footerOpen = state.preFullScreenState.footerOpen;
      state.pillarOpen = state.preFullScreenState.pillarOpen;
      state.headerOpen = state.preFullScreenState.headerOpen;
      state.previewSize = state.preFullScreenState.previewSize;
    } else {
      state.panelOpen = true;
      state.oppositePanelOpen = false;
      state.footerOpen = false;
      state.pillarOpen = true;
      state.headerOpen = true;
      state.previewSize = EPreviewSize.DESKTOP;
    }
    state.preFullScreenState = null;
  }),
  setLayoutGridState: create.reducer((state, action: PayloadAction<SetLayoutGridState>) => {
    state.direction = action.payload.direction ?? state.direction;
    state.panelOpen = updateLayoutGridStateField({ key: 'panelOpen', state, action });
    state.oppositePanelOpen = updateLayoutGridStateField({ key: 'oppositePanelOpen', state, action });
    state.footerOpen = updateLayoutGridStateField({ key: 'footerOpen', state, action });
    state.pillarOpen = updateLayoutGridStateField({ key: 'pillarOpen', state, action });
    state.headerOpen = updateLayoutGridStateField({ key: 'headerOpen', state, action });
  }),
  setTrackedBodyAttributes: create.reducer((state, action: PayloadAction<Partial<TLayoutBodyAttributes>>) => {
    return {
      ...state,
      bodyAttributes: {
        ...state.bodyAttributes,
        ...action.payload,
      },
    };
  }),
});
